import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Megaphone,
  CalendarDays,
  PenLine,
  Film,
  Palette,
  ListChecks,
  Sparkles,
  BookOpen,
  BarChart3,
  Bell,
  Users,
  Eye,
  Video,
  Search,
  LogOut,
  ChevronDown,
  Briefcase,
  FileText,
  Link2,
  Plug,
  Smartphone,
  Mail,
} from "lucide-react";
import { useAuth, ROLE_LABELS } from "../lib/auth";
import { useState } from "react";

import { Inbox } from "lucide-react";

const NAV_GROUPS = [
  {
    label: "🎯 Comando",
    items: [
      { to: "/", icon: LayoutDashboard, label: "Dashboard", testid: "nav-dashboard" },
      { to: "/mi-workspace", icon: Inbox, label: "Mi workspace", testid: "nav-workspace" },
      { to: "/aprobaciones", icon: Inbox, label: "Aprobaciones", testid: "nav-aprobaciones", roles: ["ceo", "director_marketing"] },
      { to: "/aura-ai", icon: Sparkles, label: "Aura AI", testid: "nav-aura" },
    ],
  },
  {
    label: "📈 Estrategia",
    items: [
      { to: "/campanas", icon: Megaphone, label: "Campañas", testid: "nav-campanas" },
      { to: "/calendario", icon: CalendarDays, label: "Calendario Maestro", testid: "nav-calendario" },
      { to: "/avisos", icon: Bell, label: "Avisos Estratégicos", testid: "nav-avisos" },
      { to: "/reuniones", icon: Briefcase, label: "Reuniones", testid: "nav-reuniones" },
    ],
  },
  {
    label: "🎬 Producción Creativa",
    items: [
      { to: "/briefs", icon: FileText, label: "Briefs (Diseño)", testid: "nav-briefs" },
      { to: "/guiones", icon: Film, label: "Guiones", testid: "nav-guiones" },
      { to: "/video-lab", icon: Video, label: "Video Lab", testid: "nav-video-lab" },
      { to: "/copy-studio", icon: PenLine, label: "Copy Studio", testid: "nav-copy" },
      { to: "/tareas", icon: ListChecks, label: "Tareas", testid: "nav-tareas" },
    ],
  },
  {
    label: "📲 Distribución",
    items: [
      { to: "/push-marketing", icon: Smartphone, label: "Push Marketing", testid: "nav-push" },
      { to: "/email-marketing", icon: Mail, label: "Email Marketing", testid: "nav-email" },
    ],
  },
  {
    label: "📊 Inteligencia",
    items: [
      { to: "/brand-kit", icon: BookOpen, label: "Brand Kit", testid: "nav-brand" },
      { to: "/publicaciones", icon: BarChart3, label: "Publicaciones & Métricas", testid: "nav-posts" },
      { to: "/competencia", icon: Eye, label: "Radar Competencia", testid: "nav-comp" },
    ],
  },
  {
    label: "⚙️ Equipo & Setup",
    items: [
      { to: "/creadores", icon: Users, label: "Creadores & Roles", testid: "nav-creadores" },
      { to: "/archivos", icon: Palette, label: "Archivos & UGC", testid: "nav-archivos" },
      { to: "/agendamiento", icon: Link2, label: "Agendamiento Público", testid: "nav-agendamiento" },
      { to: "/integraciones", icon: Plug, label: "Integraciones", testid: "nav-integraciones" },
    ],
  },
];

const Sidebar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  return (
    <aside className="aura-sidebar w-64 h-screen flex flex-col fixed left-0 top-0 z-40">
      <div className="px-5 py-5 border-b border-white/10 flex items-center gap-3">
        <div className="aura-logo-mark w-9 h-9 rounded-xl flex items-center justify-center font-display text-white font-bold text-lg">
          A
        </div>
        <div>
          <div className="font-display font-semibold text-white text-base leading-none">
            Aura Rubí OS
          </div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-white/40 mt-1">
            Command Center
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
        {NAV_GROUPS.map((group, idx) => (
          <div key={group.label} className={idx > 0 ? "pt-3" : ""}>
            <div className="aura-sidebar-section">{group.label}</div>
            {group.items.filter((it) => !it.roles || it.roles.includes(user?.role)).map((item) => {
              const Icon = item.icon;
              const active =
                item.to === "/"
                  ? location.pathname === "/"
                  : location.pathname.startsWith(item.to);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={item.testid}
                  className={`aura-sidebar-item ${active ? "active" : ""}`}
                >
                  <Icon size={17} strokeWidth={1.7} />
                  <span className="text-[13px]">{item.label}</span>
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      <div className="border-t border-white/10 px-3 py-3">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="w-9 h-9 rounded-full bg-[#D99D26]/20 border border-[#D99D26]/30 flex items-center justify-center text-[#D99D26] font-display font-semibold">
            {user?.name?.charAt(0).toUpperCase() || "?"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-white text-sm font-medium truncate">{user?.name}</div>
            <div className="text-white/40 text-[10px] tracking-wider uppercase truncate">
              {ROLE_LABELS[user?.role] || user?.role}
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="text-white/50 hover:text-[#D99D26] transition-colors p-2"
            title="Cerrar sesión"
          >
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </aside>
  );
};

const Topbar = ({ title, subtitle, actions }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    // Quick navigate: route shortcuts
    const q = query.toLowerCase();
    if (q.includes("campa")) navigate("/campanas");
    else if (q.includes("calendar")) navigate("/calendario");
    else if (q.includes("copy")) navigate("/copy-studio");
    else if (q.includes("guio") || q.includes("guion")) navigate("/guiones");
    else if (q.includes("brief")) navigate("/briefs");
    else if (q.includes("diseñ") || q.includes("disen")) navigate("/diseno");
    else if (q.includes("tare")) navigate("/tareas");
    else if (q.includes("aura") || q.includes("ai")) navigate("/aura-ai");
    else if (q.includes("brand")) navigate("/brand-kit");
    else if (q.includes("publica") || q.includes("metr")) navigate("/publicaciones");
    else if (q.includes("compet")) navigate("/competencia");
    else if (q.includes("creador") || q.includes("usuari")) navigate("/creadores");
    else if (q.includes("reuni")) navigate("/reuniones");
    else if (q.includes("agend") || q.includes("calend")) navigate("/agendamiento");
    else if (q.includes("integra") || q.includes("conect") || q.includes("google")) navigate("/integraciones");
    else if (q.includes("aviso") || q.includes("alert")) navigate("/avisos");
    setQuery("");
  };

  return (
    <header className="aura-topbar sticky top-0 z-30 h-16 px-8 flex items-center justify-between" data-testid="aura-topbar">
      <div className="flex items-center gap-6 flex-1">
        <div>
          <div className="label-overline">{subtitle || "Rubí Aura OS"}</div>
          <h1 className="font-display font-medium text-xl text-[#0A0A0A] leading-none mt-1">
            {title}
          </h1>
        </div>
        <form onSubmit={handleSubmit} className="ml-auto md:ml-12 max-w-md w-full hidden md:block">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#666]" />
            <input
              type="text"
              placeholder="Buscar campañas, tareas, guiones..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="global-search"
              className="aura-input pl-9 py-1.5 text-sm bg-white/60 backdrop-blur-sm"
            />
          </div>
        </form>
      </div>
      <div className="flex items-center gap-3 ml-4">
        {actions}
      </div>
    </header>
  );
};

const Layout = ({ children, title, subtitle, actions }) => {
  return (
    <div className="min-h-screen bg-[#F6F6F5]">
      <Sidebar />
      <div className="ml-64">
        <Topbar title={title} subtitle={subtitle} actions={actions} />
        <main className="px-8 py-8 max-w-[1600px]">{children}</main>
      </div>
    </div>
  );
};

export default Layout;
